import React from 'react';
import styled from '@emotion/styled';
import { X, Clock, User, Car, Calendar, Phone, Mail, MapPin, DollarSign, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import AppointmentStatusUpdateContainer from './AppointmentStatusUpdateContainer';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ModalContainer = styled.div`
  background: #1E1E35;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    border-radius: 8px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 20px 20px 0 20px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 16px 0 16px;
    margin-bottom: 12px;
  }
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #9E9E9E;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.1);
    color: #F43F60;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    padding: 6px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 4px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ModalContent = styled.div`
  padding: 0 24px 24px 24px;
  
  @media (max-width: 768px) {
    padding: 0 20px 20px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0 16px 16px 16px;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const SectionTitle = styled.h3`
  color: #A1A1B5;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 12px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 10px;
    gap: 4px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const InfoItem = styled.div`
  background: #25253C;
  border: 1px solid #25253C;
  border-radius: 8px;
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    border-radius: 4px;
  }
`;

const InfoLabel = styled.div`
  color: #9E9E9E;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 6px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 4px;
    gap: 3px;
  }
`;

const InfoValue = styled.div`
  color: #ededed;
  font-size: 1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 0.2)';
      case 'cancelled': return 'rgba(239, 68, 68, 0.2)';
      default: return 'rgba(244, 63, 96, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#22C55E';
      case 'cancelled': return '#EF4444';
      default: return '#F43F60';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 0.3)';
      case 'cancelled': return 'rgba(239, 68, 68, 0.3)';
      default: return 'rgba(244, 63, 96, 0.3)';
    }
  }};
  
  @media (max-width: 768px) {
    padding: 4px 10px;
    font-size: 0.8rem;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 3px 8px;
    font-size: 0.75rem;
    gap: 3px;
  }
`;

const ContactLink = styled.a`
  color: #4BBBE4;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    color: #4BBBE4;
    text-decoration: underline;
  }
`;

const VehicleImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  background: #181818;
  
  @media (max-width: 768px) {
    height: 150px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    height: 120px;
    border-radius: 4px;
  }
`;

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed': return <CheckCircle size={16} />;
    case 'cancelled': return <XCircle size={16} />;
    default: return <AlertCircle size={16} />;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '--';
  return timeStr;
};

const AppointmentDetailsModal = ({ appointment, isOpen, onClose }) => {
  if (!isOpen || !appointment) return null;

  console.log('appointment', appointment);
  const vehicle = appointment.offer?.vehicle || {};
  const user = appointment.offer?.user?.userInfo || {};

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Appointment Details</ModalTitle>
          <CloseButton onClick={onClose} title="Close">
            <X />
          </CloseButton>
        </ModalHeader>
        
        <ModalContent>
          {/* Status Section */}
          <Section>
            <SectionTitle>
              <AlertCircle size={20} />
              Status & Schedule
            </SectionTitle>
            <StatusContainer>
              <StatusBadge status={appointment.status}>
                {getStatusIcon(appointment.status)}
                {appointment.status?.charAt(0).toUpperCase() + appointment.status?.slice(1) || 'Unknown'}
              </StatusBadge>
              <AppointmentStatusUpdateContainer 
                appointment={appointment} 
                onStatusChange={() => {
                  // Refresh data or update parent state
                }}
              />
            </StatusContainer>
          </Section>

          {/* Schedule Information */}
          <Section>
            <SectionTitle>
              <Calendar size={20} />
              Schedule Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>
                  <Calendar size={16} />
                  Appointment Date
                </InfoLabel>
                <InfoValue>{formatDate(appointment.date)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <Clock size={16} />
                  Time Slot
                </InfoLabel>
                <InfoValue>
                  {appointment.timeSlot ? 
                    `${formatTime(appointment.timeSlot.start)} - ${formatTime(appointment.timeSlot.end)}` : 
                    '--'
                  }
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          {/* Customer Information */}
          <Section>
            <SectionTitle>
              <User size={20} />
              Customer Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>
                  <User size={16} />
                  Customer Name
                </InfoLabel>
                <InfoValue>{user.name || '--'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <Mail size={16} />
                  Email Address
                </InfoLabel>
                <InfoValue>
                  {user.email ? (
                    <ContactLink href={`mailto:${user.email}`}>
                      {user.email}
                    </ContactLink>
                  ) : '--'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <Phone size={16} />
                  Phone Number
                </InfoLabel>
                <InfoValue>
                  {user.phone ? (
                    <ContactLink href={`tel:${user.phone}`}>
                      {user.phone}
                    </ContactLink>
                  ) : '--'}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          {/* Vehicle Information */}
          <Section>
            <SectionTitle>
              <Car size={20} />
              Vehicle Information
            </SectionTitle>
            
            {/* Vehicle Image */}
            {vehicle.images && vehicle.images.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <VehicleImage 
                  src={vehicle.images[0].url} 
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  onError={(e) => {
                    e.target.src = '/car-placeholder.png';
                  }}
                />
              </div>
            )}
            
            <InfoGrid>
              <InfoItem>
                <InfoLabel>
                  <Car size={16} />
                  Vehicle
                </InfoLabel>
                <InfoValue>
                  {vehicle.year && vehicle.make && vehicle.model ? 
                    `${vehicle.year} ${vehicle.make} ${vehicle.model}` : '--'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <Car size={16} />
                  Trim Level
                </InfoLabel>
                <InfoValue>{vehicle.trim || '--'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <Car size={16} />
                  Stock Number
                </InfoLabel>
                <InfoValue>{vehicle.stockId || '--'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <Car size={16} />
                  VIN
                </InfoLabel>
                <InfoValue>{vehicle.vin || '--'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <DollarSign size={16} />
                  Offer Amount
                </InfoLabel>
                <InfoValue>
                  {appointment.offer?.amount ? 
                    `$${appointment.offer.amount.toLocaleString()}` : '--'}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <DollarSign size={16} />
                  Asking Price
                </InfoLabel>
                <InfoValue>
                  {vehicle.askingPrice ? 
                    `$${vehicle.askingPrice.toLocaleString()}` : '--'}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          {/* Additional Vehicle Details */}
          {(vehicle.mileage || vehicle.engine || vehicle.transmission || vehicle.exteriorColor) && (
            <Section>
              <SectionTitle>
                <Car size={20} />
                Additional Details
              </SectionTitle>
              <InfoGrid>
                {vehicle.mileage && (
                  <InfoItem>
                    <InfoLabel>
                      <Car size={16} />
                      Mileage
                    </InfoLabel>
                    <InfoValue>{vehicle.mileage.toLocaleString()} miles</InfoValue>
                  </InfoItem>
                )}
                {vehicle.engine && (
                  <InfoItem>
                    <InfoLabel>
                      <Car size={16} />
                      Engine
                    </InfoLabel>
                    <InfoValue>{vehicle.engine}</InfoValue>
                  </InfoItem>
                )}
                {vehicle.transmission && (
                  <InfoItem>
                    <InfoLabel>
                      <Car size={16} />
                      Transmission
                    </InfoLabel>
                    <InfoValue>{vehicle.transmission}</InfoValue>
                  </InfoItem>
                )}
                {vehicle.exteriorColor && (
                  <InfoItem>
                    <InfoLabel>
                      <Car size={16} />
                      Exterior Color
                    </InfoLabel>
                    <InfoValue>{vehicle.exteriorColor}</InfoValue>
                  </InfoItem>
                )}
                {vehicle.interiorColor && (
                  <InfoItem>
                    <InfoLabel>
                      <Car size={16} />
                      Interior Color
                    </InfoLabel>
                    <InfoValue>{vehicle.interiorColor}</InfoValue>
                  </InfoItem>
                )}
                {vehicle.isCertified && (
                  <InfoItem>
                    <InfoLabel>
                      <CheckCircle size={16} />
                      Certification
                    </InfoLabel>
                    <InfoValue>Certified Vehicle</InfoValue>
                  </InfoItem>
                )}
              </InfoGrid>
            </Section>
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default AppointmentDetailsModal; 